import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle, Shield, Wrench, Phone, Car, DollarSign, Clock, ChevronRight } from 'lucide-react';
import { vehicleService } from '../../services/vehicleService';
import { formatVehicleTitle } from '../../utils/textFormatting';

const BMWInventory = () => {
  const [bmwVehicles, setBmwVehicles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadBmwInventory = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await vehicleService.getAvailableVehicles();
        if (!error && data) {
          const filtered = data.filter(v => {
            const make = (v.make || '').toLowerCase();
            return make === 'bmw';
          });
          setBmwVehicles(filtered);
        }
      } catch (err) {
        console.error('Error loading BMW inventory:', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadBmwInventory();
  }, []);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatMileage = (mileage) => {
    return new Intl.NumberFormat('en-US').format(mileage);
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <>
      <Helmet>
        <title>Used BMW for Sale Dallas-Fort Worth - Pre-Owned BMW Inventory</title>
        <meta name="description" content="Shop pre-owned BMWs in DFW. Every BMW inspected by European car specialists before listing. 3 Series, 5 Series, X3, X5 and M cars. Financing available. Call (817) 382-2316." />
        <link rel="canonical" href="https://paradise-performance.com/inventory/bmw" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://paradise-performance.com/inventory/bmw" />
        <meta property="og:title" content="Pre-Owned BMW Inventory - Paradise, TX" />
        <meta property="og:description" content="European car mechanics who know BMWs inside and out. We inspect them, fix what needs fixing and only sell the ones worth owning." />
        <meta property="og:image" content="https://paradise-performance.com/assets/images/bmw-repair.webp" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Pre-Owned BMW Inventory - Paradise, TX" />
        <meta name="twitter:description" content="European car mechanics who know BMWs inside and out. We inspect them, fix what needs fixing and only sell the ones worth owning." />
        <meta name="twitter:image" content="https://paradise-performance.com/assets/images/bmw-repair.webp" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AutoDealer",
            "name": "Paradise-Performance LLC",
            "description": "Pre-owned BMW vehicles inspected by European car specialists",
            "telephone": "(817) 382-2316",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "305 Co Rd 3470",
              "addressLocality": "Paradise",
              "addressRegion": "TX",
              "postalCode": "76073"
            },
            "areaServed": "Dallas-Fort Worth",
            "brand": "BMW"
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Are used BMWs expensive to maintain?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "At a dealership, yes. At an independent European specialist, BMW maintenance costs drop significantly. Oil changes run $90-$130 instead of $200+. Brake jobs cost 30-40% less. The key is finding a shop that knows these cars and uses quality parts. Maintenance on a BMW doesn't have to break you if you have the right relationship with your mechanic."
                }
              },
              {
                "@type": "Question",
                "name": "What should I check before buying a used BMW?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Cooling system condition is the big one. BMWs use plastic coolant components that degrade over time. Check for oil leaks around the valve cover gasket and oil filter housing. On turbocharged models, verify the charge pipe and wastegate rattle. Pull the ISTA diagnostic report for stored fault codes. We check all of this before any BMW hits our lot."
                }
              },
              {
                "@type": "Question",
                "name": "Do you offer financing on used BMWs?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes. We offer in-house financing on select vehicles. The application takes about five minutes online. Most approvals come back within 24 hours."
                }
              },
              {
                "@type": "Question",
                "name": "Why buy a used BMW from a repair shop instead of a dealership?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "A dealership sales team picks cars based on profit margins and auction prices. We pick cars based on mechanical condition because our reputation depends on it. Every BMW we sell is inspected by the same technicians who repair them daily. You build a relationship with the shop that sold you the car and services it going forward."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen bg-gray-50 pt-24 lg:pt-36 md:pt-16 sm:pt-20 pb-20"
      >
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-[#1C1C1C] to-[#333333] text-white py-16 mb-12 overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="md:flex items-center gap-8 max-w-6xl mx-auto">
              <div className="flex-1 text-center md:text-left mb-8 md:mb-0">
                <p className="text-gray-400 text-sm font-semibold uppercase tracking-wider mb-3">Paradise-Performance LLC</p>
                <h1 className="text-4xl md:text-5xl font-bold mb-6">Pre-Owned BMWs Vetted by the Mechanics Who Fix Them</h1>
                <p className="text-xl text-gray-300 mb-8 max-w-xl">
                  We don't buy BMWs at auction and hope for the best. Every car gets a full inspection from techs who work on these engines every day. No surprises after the sale.
                </p>
                <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                  <a
                    href="#inventory"
                    className="bg-[#0066B1] text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#0077CC] transition"
                  >
                    Browse BMW Inventory
                  </a>
                  <a
                    href="tel:8173822316"
                    className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white/10 transition"
                  >
                    Call (817) 382-2316
                  </a>
                </div>
              </div>
              <div className="flex-shrink-0 w-full md:w-[400px] flex items-center justify-center">
                <img
                  src="/assets/assets/images/bmw-repair.webp"
                  alt="BMW M4 Front View"
                  className="w-full max-w-[400px] rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4">
          {/* Why Buy a BMW From Us */}
          <motion.div {...fadeInUp} className="mb-16">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">The Shop That Fixes Them Also Sells Them</h2>
              <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
                <p>
                  Buying a used BMW is a trust exercise. You're putting money down on a car that most people think is expensive to own. And honestly, it can be — if you buy the wrong one from the wrong place. That's the whole reason we started selling them.
                </p>
                <p>
                  We're a European repair shop first. BMWs roll through our bays all week long. N54 turbos with wastegate rattle. N55s leaking oil from the valve cover. B58s that need cooling system overhauls at 80,000 miles. We've seen every failure mode these engines produce. So when we source a BMW to sell, we already know where to look.
                </p>
                <p>
                  A typical used car lot runs a Carfax and checks the tires. We pull the car into the shop and treat it like a customer's repair. Full diagnostic scan with BMW-specific software. We check the cooling system, the turbo system and the transmission adaptation data. If something's off, we fix it before it goes on the lot. If the car isn't worth the investment, it goes back to auction.
                </p>
                <p>
                  The relationship doesn't end when you drive off. You bought the car from us and we service it going forward. Same techs. Same shop. We're not disappearing after the handshake. That's a very different experience from buying off a franchise lot where nobody remembers your name two weeks later.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Inspection Process */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Our BMW Inspection Before It Hits the Lot</h2>
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Wrench className="w-6 h-6 text-[#0066B1]" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Engine and Turbo System</h3>
                <ul className="text-gray-600 space-y-2 text-sm">
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Full ISTA diagnostic scan with fault code history</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Turbo wastegate rattle check and boost pressure test</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> VANOS solenoid and timing chain condition</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Oil consumption monitoring over test drive period</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Charge pipe integrity on turbo models</li>
                </ul>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-[#0066B1]" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Cooling and Electrical</h3>
                <ul className="text-gray-600 space-y-2 text-sm">
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Coolant expansion tank and hose inspection</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Water pump and thermostat function test</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Radiator pressure test for micro-leaks</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Battery health and charging system verification</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> All module communication check (no sleeping modules)</li>
                </ul>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Car className="w-6 h-6 text-[#0066B1]" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">BMW-Specific Weak Points</h3>
                <ul className="text-gray-600 space-y-2 text-sm">
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Valve cover gasket and oil filter housing gasket</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Oil pan gasket condition (common slow leak)</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Rear main seal inspection</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Window regulator and door lock actuator function</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Suspension bushings and control arm ball joints</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* BMW Models Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">BMW Models We Typically Stock</h2>
              <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
                <p>
                  We don't chase trends or stock whatever's cheap at auction. If a BMW comes through in solid mechanical shape at the right price, we grab it. Some models show up more than others.
                </p>
                <p>
                  <strong>3 Series (F30/G20)</strong> — The bread and butter. The 328i and 330i are the sweet spot for daily driving. Enough power to be fun, great fuel economy and a refined ride. The B48 four-cylinder in newer models is surprisingly durable. These are easy to trust for a daily commuter.
                </p>
                <p>
                  <strong>5 Series (F10/G30)</strong> — The executive sedan that BMW built its reputation on. Highway comfort is hard to beat. The 528i/530i models give you the luxury feel without the six-cylinder maintenance bill. The 540i is a rocket with the B58 inline-six if you want more punch.
                </p>
                <p>
                  <strong>X3 (F25/G01)</strong> — BMW's best-selling SUV for a reason. Practical size for families without feeling like a boat in a parking lot. The xDrive30i is the volume model and it's a solid all-rounder. We pay close attention to the transfer case and front differential seals on these.
                </p>
                <p>
                  <strong>X5 (F15/G05)</strong> — The big one. Three-row capability on some trims. The 40i with the inline-six is the one to get if you can find it — smooth power delivery and it tows reasonably well. We inspect the air suspension thoroughly on every X5 that comes through.
                </p>
                <p>
                  <strong>M Cars (M2/M3/M4)</strong> — Performance machines. These get driven hard and that's fine. We just make sure they've been maintained equally hard. Rod bearings on the S55 get checked. Crank hub on S55s gets inspected. We won't sell an M car with deferred maintenance — too much risk for both sides of that relationship.
                </p>
                <p>
                  <strong>4 Series (F32/G22)</strong> — The coupe version of the 3 Series with a wider stance and sportier feel. The 430i is a great looking car that doesn't cost M money to maintain. The M440i splits the difference between daily driver and weekend toy.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Live Inventory Section */}
          <div id="inventory">
            <motion.div {...fadeInUp} className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">BMWs Currently in Stock</h2>
              <p className="text-lg text-gray-600 text-center mb-8 max-w-3xl mx-auto">
                Every BMW listed here passed our shop inspection. What you see is what's on the lot right now.
              </p>

              {isLoading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0066B1] mx-auto mb-4"></div>
                  <p className="text-gray-500">Loading BMW inventory...</p>
                </div>
              ) : bmwVehicles.length > 0 ? (
                <>
                  <style>{`
                    .bmw-inv-card {
                      position: relative;
                      background: linear-gradient(135deg, #e0e0e0, #c0c0c0);
                      border-radius: 16px;
                      box-shadow:
                        4px 4px 8px rgba(0, 0, 0, 0.2),
                        -4px -4px 8px rgba(255, 255, 255, 0.2),
                        inset 1px 1px 2px rgba(255, 255, 255, 0.3),
                        inset -1px -1px 2px rgba(0, 0, 0, 0.3);
                      transition: transform 0.3s ease, box-shadow 0.3s ease;
                      overflow: hidden;
                      display: flex;
                      flex-direction: column;
                    }
                    @media (min-width: 768px) {
                      .bmw-inv-card {
                        border-radius: 24px;
                        box-shadow:
                          6px 6px 12px rgba(0, 0, 0, 0.3),
                          -6px -6px 12px rgba(255, 255, 255, 0.3),
                          inset 2px 2px 4px rgba(255, 255, 255, 0.4),
                          inset -2px -2px 4px rgba(0, 0, 0, 0.5);
                      }
                    }
                    .bmw-inv-card::before {
                      content: "";
                      position: absolute;
                      top: 0; left: 0; width: 100%; height: 100%;
                      background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.7), transparent);
                      background-size: 200% 200%;
                      animation: bmwinvshine 3s infinite ease-in-out;
                      border-radius: inherit;
                      pointer-events: none;
                      z-index: 1;
                    }
                    .bmw-inv-card:hover {
                      transform: translateY(-5px);
                      box-shadow:
                        8px 8px 16px rgba(0, 0, 0, 0.35),
                        -8px -8px 16px rgba(255, 255, 255, 0.35),
                        inset 2px 2px 4px rgba(255, 255, 255, 0.5),
                        inset -2px -2px 4px rgba(0, 0, 0, 0.6);
                    }
                    .bmw-inv-content {
                      position: relative;
                      z-index: 2;
                      flex: 1;
                      display: flex;
                      flex-direction: column;
                    }
                    .bmw-inv-img {
                      position: relative;
                      border-radius: 16px 16px 0 0;
                      overflow: hidden;
                      background: linear-gradient(135deg, #f0f0f0, #d0d0d0);
                      box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.2);
                    }
                    @media (min-width: 768px) {
                      .bmw-inv-img {
                        border-radius: 24px 24px 0 0;
                      }
                    }
                    .bmw-inv-btn {
                      background: linear-gradient(135deg, #0066B1, #0088DD);
                      box-shadow:
                        2px 2px 4px rgba(0, 0, 0, 0.3),
                        -2px -2px 4px rgba(255, 255, 255, 0.3),
                        inset 1px 1px 2px rgba(255, 255, 255, 0.3);
                      border: none;
                      border-radius: 12px;
                      transition: all 0.3s ease;
                    }
                    .bmw-inv-btn:hover {
                      background: linear-gradient(135deg, #0077CC, #0099EE);
                      box-shadow:
                        3px 3px 6px rgba(0, 0, 0, 0.35),
                        -3px -3px 6px rgba(255, 255, 255, 0.35),
                        inset 1px 1px 2px rgba(255, 255, 255, 0.4);
                      transform: translateY(-1px);
                    }
                    @keyframes bmwinvshine {
                      0% { background-position: 0% 50%; }
                      50% { background-position: 100% 50%; }
                      100% { background-position: 0% 50%; }
                    }
                  `}</style>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {bmwVehicles.map((vehicle) => (
                      <motion.div
                        key={vehicle.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bmw-inv-card"
                      >
                        <div className="bmw-inv-content">
                          <div className="bmw-inv-img">
                            <img
                              src={vehicle.images?.[0] || '/assets/assets/images/bmw-repair.webp'}
                              alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                              className="w-full h-52 object-cover"
                              onError={(e) => { e.target.src = '/assets/assets/images/bmw-repair.webp'; }}
                            />
                            <div className="absolute top-3 left-3 flex flex-col gap-2 z-10">
                              <span className={`px-3 py-1 rounded-full text-xs font-bold shadow-lg ${
                                vehicle.condition === 'new' ? 'bg-gradient-to-r from-green-400 to-green-600 text-white' :
                                'bg-gradient-to-r from-blue-400 to-blue-600 text-white'
                              }`}>
                                {vehicle.condition === 'new' ? 'New' : 'Used'}
                              </span>
                              {vehicle.status === 'on-lot' && (
                                <span className="px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-green-400 to-green-600 text-white shadow-lg">
                                  Available
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="p-5 flex flex-col flex-1">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <h3 className="text-lg font-bold text-gray-800">{formatVehicleTitle(vehicle)}</h3>
                                {vehicle.stockNumber && (
                                  <p className="text-sm text-gray-500">Stock #{vehicle.stockNumber}</p>
                                )}
                              </div>
                              <div className="text-right">
                                <div className="text-xl font-bold bg-gradient-to-br from-blue-600 to-blue-800 bg-clip-text text-transparent">
                                  {formatPrice(vehicle.price)}
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center text-sm text-gray-600 mb-3 space-x-3">
                              {vehicle.mileage > 0 && <span>{formatMileage(vehicle.mileage)} mi</span>}
                              {vehicle.mileage > 0 && vehicle.transmission && <span>•</span>}
                              {vehicle.transmission && <span>{vehicle.transmission}</span>}
                              {vehicle.fuelType && <><span>•</span><span>{vehicle.fuelType}</span></>}
                            </div>

                            {vehicle.features && vehicle.features.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 mb-4">
                                {vehicle.features.slice(0, 3).map((feature, index) => (
                                  <span key={index} className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded-md font-medium">
                                    {feature}
                                  </span>
                                ))}
                                {vehicle.features.length > 3 && (
                                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-md font-bold">
                                    +{vehicle.features.length - 3} more
                                  </span>
                                )}
                              </div>
                            )}

                            <div className="flex space-x-2 mt-auto">
                              <Link
                                to={`/vehicle/${vehicle.id}`}
                                className="flex-1 bmw-inv-btn text-white px-4 py-3 font-bold text-center text-sm"
                              >
                                View Details
                              </Link>
                              <a
                                href="tel:8173822316"
                                className="bmw-inv-btn text-white px-4 py-3 font-bold flex items-center justify-center"
                                title="Call about this BMW"
                              >
                                <Phone className="w-4 h-4" />
                              </a>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  <div className="text-center mt-8">
                    <Link
                      to="/inventory"
                      className="inline-block bg-gray-200 text-gray-800 px-8 py-3 rounded-lg font-bold hover:bg-gray-300 transition"
                    >
                      Browse Full Inventory →
                    </Link>
                  </div>
                </>
              ) : (
                <div className="bg-white rounded-xl shadow-lg p-8 text-center max-w-2xl mx-auto">
                  <Car className="w-12 h-12 text-blue-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-800 mb-2">No BMWs in Stock Right Now</h3>
                  <p className="text-gray-600 mb-6">
                    Our BMW inventory turns over fast. Give us a call and tell us what you're after. We source vehicles weekly and can keep an eye out for the right one.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <a href="tel:8173822316" className="bg-[#0066B1] text-white px-6 py-3 rounded-lg font-bold hover:bg-[#0077CC] transition">
                      Call (817) 382-2316
                    </a>
                    <Link to="/inventory" className="bg-gray-200 text-gray-800 px-6 py-3 rounded-lg font-bold hover:bg-gray-300 transition">
                      Browse All Inventory
                    </Link>
                  </div>
                </div>
              )}
            </motion.div>
          </div>

          {/* Trust Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Why the Place You Buy From Matters</h2>
              <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
                <p>
                  BMWs reward owners who stay on top of maintenance. They punish owners who ignore it. The difference between a great used BMW and a money pit usually comes down to one thing — the first 30 days of ownership. If something was hiding during the sale, you find it fast.
                </p>
                <p>
                  That's why trust matters more with a BMW than almost any other brand. You need to know that whoever sold it to you actually checked the car. Not a cursory glance. A real inspection by someone who understands the platform. Our techs work on BMWs five days a week. They know the N54's weak points, the N55's oil leak patterns and the B58's cooling quirks. That knowledge goes into every car we list.
                </p>
                <p>
                  After the sale, we're still your shop. That ongoing relationship means we're not incentivized to hide problems — we'd just end up fixing them later on our own dime. It's simpler to do it right the first time. You trust us with the purchase and we earn that trust every time the car comes back for service.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Financing CTA */}
          <motion.div {...fadeInUp} className="mb-16">
            <div className="bg-white rounded-xl shadow-lg p-8 md:p-12 max-w-4xl mx-auto">
              <div className="md:flex items-center gap-8">
                <div className="flex-1 mb-6 md:mb-0">
                  <h2 className="text-2xl font-bold text-gray-900 mb-3">Financing Available on Select BMWs</h2>
                  <p className="text-gray-600 mb-4">
                    We offer in-house financing on qualifying vehicles. Five minutes to apply. Most approvals come back within 24 hours.
                  </p>
                  <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1"><DollarSign className="w-4 h-4 text-green-500" /> Competitive rates</span>
                    <span className="flex items-center gap-1"><Clock className="w-4 h-4 text-blue-500" /> Quick approval</span>
                    <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4 text-green-500" /> No obligation</span>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <Link
                    to="/finance-application"
                    className="inline-block bg-[#0066B1] text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#0077CC] transition"
                  >
                    Apply for Financing
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>

          {/* FAQ Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Common Questions About Buying a Used BMW</h2>
            <div className="max-w-3xl mx-auto space-y-6">
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Are used BMWs expensive to maintain?</h3>
                <p className="text-gray-700">
                  At a dealership, yes. At an independent European specialist, the costs drop fast. Oil changes run $90–$130 instead of $200+. Brake jobs cost 30–40% less. The key is finding a shop that knows these cars and uses quality parts. BMW maintenance doesn't have to break you if you have the right relationship with your mechanic.
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">What should I check before buying a used BMW?</h3>
                <p className="text-gray-700">
                  Cooling system is the big one. BMWs use plastic coolant components that degrade with heat and age. Check for oil leaks around the valve cover gasket and oil filter housing. On turbo models, verify the charge pipe and listen for wastegate rattle. Pull the diagnostic report for stored fault codes. We check all of this before any BMW makes our lot.
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Do you offer financing on used BMWs?</h3>
                <p className="text-gray-700">
                  Yes. We offer in-house financing on select vehicles. Fill out our <Link to="/finance-application" className="text-[#0066B1] font-semibold hover:underline">online finance application</Link> to get started. Approval typically takes less than 24 hours.
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Why buy a used BMW from a repair shop?</h3>
                <p className="text-gray-700">
                  A dealership sales team picks inventory based on profit margins and auction prices. We pick based on mechanical condition because our reputation depends on it. Every BMW we sell is inspected by the same technicians who repair them daily. You build a relationship with the shop that sold you the car — and that shop services it going forward. That's a level of trust a franchise lot can't offer.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Cross-link to BMW Repair */}
          <motion.div {...fadeInUp} className="mb-16">
            <div className="bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl p-8 md:p-12 max-w-4xl mx-auto">
              <div className="md:flex items-center gap-8">
                <div className="flex-1 mb-6 md:mb-0">
                  <h2 className="text-2xl font-bold text-gray-900 mb-3">Already Own a BMW?</h2>
                  <p className="text-gray-700">
                    Our shop handles everything from oil changes to full engine rebuilds. Same technicians. Same BMW expertise. Check out our dedicated repair page for service details.
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <Link
                    to="/services/european-car-repair/bmw"
                    className="inline-flex items-center gap-2 bg-[#0066B1] text-white px-6 py-3 rounded-lg font-bold hover:bg-[#0077CC] transition"
                  >
                    BMW Repair Services <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Final CTA */}
          <motion.div {...fadeInUp} className="text-center bg-gradient-to-r from-[#1C1C1C] to-[#333333] rounded-xl p-12 text-white">
            <h2 className="text-4xl font-bold mb-4">Ready to Drive a BMW Home?</h2>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Stop by the lot or give us a call. No pressure. Just honest answers from the people who actually work on these cars.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a
                href="tel:8173822316"
                className="bg-[#0066B1] text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#0077CC] transition"
              >
                Call (817) 382-2316
              </a>
              <Link
                to="/contact"
                className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white/10 transition"
              >
                Contact Us
              </Link>
            </div>
            <p className="mt-6 text-gray-400">305 Co Rd 3470, Paradise, TX 76073 — Serving Dallas-Fort Worth</p>
          </motion.div>
        </div>
      </motion.div>
    </>
  );
};

export default BMWInventory;
